import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TeamProvider } from './context/TeamContext';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { ThemeProvider } from './context/ThemeContext';
import { ParentAuthProvider, useParentAuth } from './context/ParentAuthContext';
import { Layout } from './components/Layout';
import MiCoachLogo from './components/MiCoachLogo';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import CookieBanner from './components/CookieBanner';
import { InstallBanner } from './components/InstallApp';
import { applyStoredConsent } from './lib/consent';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import PublicSessionPage from './pages/PublicSessionPage';
import DashboardPage from './pages/DashboardPage';
import { detectTenant } from './lib/tenantConfig';
import { isNativeApp } from './lib/platform';
import { isStandalone, getLastPortal } from './lib/pwa';
import { getEnterpriseRole, getHomeRoute, shouldShowEnterpriseNav, ROLE_KEYS } from './lib/enterpriseRoles';
import './index.css';

// Páginas legales (lazy, públicas)
const AvisoLegalPage        = lazy(() => import('./pages/legal/AvisoLegalPage'));
const PrivacidadPage        = lazy(() => import('./pages/legal/PrivacidadPage'));
const CookiesPage           = lazy(() => import('./pages/legal/CookiesPage'));
const TerminosPage          = lazy(() => import('./pages/legal/TerminosPage'));

// Portal de Padres (lazy)
const ParentInvitePage      = lazy(() => import('./pages/parent/ParentInvitePage'));
const ParentHomePage        = lazy(() => import('./pages/parent/ParentHomePage'));
const ParentMessageDetail   = lazy(() => import('./pages/parent/ParentMessageDetailPage'));
const ParentMatchCallPage   = lazy(() => import('./pages/parent/ParentMatchCallPage'));
const ParentSettingsPage    = lazy(() => import('./pages/parent/ParentSettingsPage'));
const ParentNotFoundPage    = lazy(() => import('./pages/parent/ParentNotFoundPage'));
const ParentLoginPage       = lazy(() => import('./pages/parent/ParentLoginPage'));
const ParentsPanelPage      = lazy(() => import('./pages/ParentsPanelPage'));

// Enterprise pages
const EnterpriseLoginPage = lazy(() => import('./pages/EnterpriseLoginPage'));
const EnterpriseSuperadminPage = lazy(() => import('./pages/EnterpriseSuperadminPage'));
const AcademyDetailPage = lazy(() => import('./pages/AcademyDetailPage'));
const EnterpriseFormationPage = lazy(() => import('./pages/EnterpriseFormationPage'));
const EnterpriseNotificationsPage = lazy(() => import('./pages/EnterpriseNotificationsPage'));
const EnterprisePaymentsPage = lazy(() => import('./pages/EnterprisePaymentsPage'));
const EnterpriseMetricsPage = lazy(() => import('./pages/EnterpriseMetricsPage'));

// Lazy loaded pages
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const VerifyCodePage = lazy(() => import('./pages/VerifyCodePage'));
const AdminApprovePage = lazy(() => import('./pages/AdminApprovePage'));
const LogoutPage = lazy(() => import('./pages/LogoutPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const CreateTeamPage = lazy(() => import('./pages/CreateTeamPage'));
const MicrocicloPage = lazy(() => import('./pages/MicrocicloPage'));
const SessionBuilderPage = lazy(() => import('./pages/SessionBuilderPage'));
const SessionsListPage = lazy(() => import('./pages/SessionsListPage'));
const GymSessionPage = lazy(() => import('./pages/GymSessionPage'));
const WellnessPage = lazy(() => import('./pages/WellnessPage'));
const SquadPage = lazy(() => import('./pages/SquadPage'));
const PlayerDetailPage = lazy(() => import('./pages/PlayerDetailPage'));
const MatchesPage = lazy(() => import('./pages/MatchesPage'));
const MatchPlanPage = lazy(() => import('./pages/MatchPlanPage'));
const VideosPage = lazy(() => import('./pages/VideosPage'));
const WeeklySummaryPage = lazy(() => import('./pages/WeeklySummaryPage'));
const TacticalBoardPage = lazy(() => import('./pages/TacticalBoardPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const NutritionELearningPage = lazy(() => import('./pages/NutritionELearningPage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));
const CoordinatorDashboard = lazy(() => import('./pages/CoordinatorDashboard'));
const OrganizationSettings = lazy(() => import('./pages/OrganizationSettings'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const TeamStaffPage = lazy(() => import('./pages/TeamStaffPage'));
const TeamInviteAcceptPage = lazy(() => import('./pages/TeamInviteAcceptPage'));
const EliteSessionBuilder = lazy(() => import('./pages/EliteSessionBuilder'));
const TacticsLibraryPage = lazy(() => import('./pages/TacticsLibraryPage'));
const VestuarioPage = lazy(() => import('./pages/VestuarioPage'));
const SessionViewPage = lazy(() => import('./pages/SessionViewPage'));
const StatsPage = lazy(() => import('./pages/StatsPage'));
const LiveMatchPage = lazy(() => import('./pages/LiveMatchPage'));
const FinancePage = lazy(() => import('./pages/FinancePage'));

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-zinc-500">Cargando...</p>
    </div>
  </div>
);

// Única ruta accesible con el muro de pago bloqueando. No se abre /ajustes
// porque su Layout carga equipos y el 402 rebotaría al usuario igualmente; el
// propio muro lleva el botón de cerrar sesión.

const ProtectedRoute = ({ children, adminOnly = false, bare = false }) => {
  const { isAuthenticated, loading, isAdmin, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-0)' }}>
        <div className="flex flex-col items-center gap-4">
          <MiCoachLogo size={40} variant="on-emerald" />
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to={'/login?redirect=' + encodeURIComponent(location.pathname)} replace />;
  // Un familiar nunca accede al área de entrenador: lo enviamos a su portal.
  if (user?.role === 'parent') return <Navigate to="/parent/mensajes" replace />;
  // Muro de pago: sin suscripción activa no se entra. El backend lo aplica de
  // verdad (402); esto solo evita que el usuario vea una app llena de errores.
  // /onboarding se excluye para no entrar en bucle con su propio guardián.
  if (user?.access?.blocked && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;

  // `bare`: pantallas que no deben enseñar el armazón de la app (el muro de
  // pago, sin ir más lejos: su menú lateral solo lleva a sitios bloqueados).
  if (bare) return <ErrorBoundary>{children}</ErrorBoundary>;
  return <Layout><ErrorBoundary>{children}</ErrorBoundary></Layout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const [searchParams] = useSearchParams();
  const tenant = detectTenant();
  
  // Determine redirect based on role and tenant using centralized logic
  let redirectTo = searchParams.get('redirect') || '/dashboard';
  if (tenant && isAuthenticated && user) {
    // Use centralized role logic - pass true for enterprise context
    redirectTo = getHomeRoute(user, true);
  }
  // Un familiar autenticado en el contexto de coach va a su portal, no al dashboard.
  if (isAuthenticated && user?.role === 'parent') {
    redirectTo = '/parent/mensajes';
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-0)' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to={redirectTo} replace />;
  return children;
};

// Entrada en "/": en la app nativa y en la PWA instalada, si ya hay sesión,
// salta la landing y va directo al área del usuario (entrenador -> dashboard,
// familiar -> su portal). Y si no hay sesión pero este dispositivo ya la tuvo
// alguna vez, se va al login en lugar de a la landing de captación: quien ya
// es usuario no debería toparse con "empezar gratis" cada vez que sale.
//
// En la web abierta como pestaña se mantiene la landing como hasta ahora: ahí
// sí es la puerta de entrada para quien todavía no conoce el producto.
function HomeEntry() {
  const { isAuthenticated, user } = useAuth();
  const { isAuthenticated: parentAuthenticated } = useParentAuth();
  if (isNativeApp() || isStandalone()) {
    if (parentAuthenticated || user?.role === 'parent') {
      return <Navigate to="/parent/mensajes" replace />;
    }
    if (isAuthenticated) {
      return <Navigate to="/dashboard" replace />;
    }
    const lastPortal = getLastPortal();
    if (lastPortal === 'parent') return <Navigate to="/parent/acceso" replace />;
    if (lastPortal === 'coach') return <Navigate to="/login" replace />;
  }
  return <LandingPage />;
}

function AppRoutes() {
  const tenant = detectTenant();
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={tenant ? <Navigate to="/login" replace /> : <HomeEntry />} />
        <Route path="/login" element={
          <PublicRoute>
            {tenant ? <EnterpriseLoginPage tenant={tenant} /> : <LoginPage />}
          </PublicRoute>
        } />
        <Route path="/verificar" element={<VerifyEmailPage />} />
        <Route path="/verificar-codigo" element={<VerifyCodePage />} />
        <Route path="/admin/aprobar" element={<AdminApprovePage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* ── Legales ───────────────────────────────────────────────────── */}
        {/* Públicas a propósito: se enlazan desde el registro y desde la
            invitación a familias, antes de que exista cuenta. No envolver
            en ProtectedRoute ni en PublicRoute (PublicRoute redirige a
            /dashboard si hay sesión, y un usuario logueado debe poder
            consultarlas). */}
        <Route path="/aviso-legal" element={<AvisoLegalPage />} />
        <Route path="/privacidad"  element={<PrivacidadPage />} />
        <Route path="/cookies"     element={<CookiesPage />} />
        <Route path="/terminos"    element={<TerminosPage />} />
        {/* Sesión compartida (pública, solo lectura, por token no adivinable) */}
        <Route path="/s/:token" element={<PublicSessionPage />} />
        {/* ──────────────────────────────────────────────────────────────── */}

        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute bare><OnboardingPage /></ProtectedRoute>} />
        <Route path="/equipos/nuevo" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
        <Route path="/equipos/:teamId/editar" element={<ProtectedRoute><CreateTeamPage /></ProtectedRoute>} />
        <Route path="/microciclo" element={<ProtectedRoute><MicrocicloPage /></ProtectedRoute>} />
        <Route path="/sesiones" element={<ProtectedRoute><SessionsListPage /></ProtectedRoute>} />
        <Route path="/sesiones/nueva" element={<ProtectedRoute><SessionBuilderPage /></ProtectedRoute>} />
        <Route path="/sesiones/elite" element={<ProtectedRoute><EliteSessionBuilder /></ProtectedRoute>} />
        <Route path="/sesiones/elite/:id" element={<ProtectedRoute><EliteSessionBuilder /></ProtectedRoute>} />
        <Route path="/sesiones/:id" element={<ProtectedRoute><SessionViewPage /></ProtectedRoute>} />
        <Route path="/sesiones/:id/editar" element={<ProtectedRoute><SessionBuilderPage /></ProtectedRoute>} />
        <Route path="/gimnasio" element={<ProtectedRoute><GymSessionPage /></ProtectedRoute>} />
        <Route path="/wellness" element={<ProtectedRoute><WellnessPage /></ProtectedRoute>} />
        <Route path="/squad" element={<ProtectedRoute><SquadPage /></ProtectedRoute>} />
        <Route path="/squad/staff" element={<ProtectedRoute><TeamStaffPage /></ProtectedRoute>} />
        {/* Aceptar la invitación a un equipo. La notificación apuntaba aquí y
            esta ruta no existía: el invitado acababa en el panel sin unirse. */}
        <Route path="/teams/invite/accept/:token" element={<ProtectedRoute><TeamInviteAcceptPage /></ProtectedRoute>} />
        <Route path="/jugadores/:id" element={<ProtectedRoute><PlayerDetailPage /></ProtectedRoute>} />
        <Route path="/informes" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/partidos" element={<ProtectedRoute><MatchesPage /></ProtectedRoute>} />
        <Route path="/partidos/:id/plan" element={<ProtectedRoute><MatchPlanPage /></ProtectedRoute>} />
        <Route path="/partidos/:id/vivo" element={<ProtectedRoute><LiveMatchPage /></ProtectedRoute>} />
        <Route path="/estadisticas" element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
        <Route path="/caja" element={<ProtectedRoute><FinancePage /></ProtectedRoute>} />
        <Route path="/videos" element={<ProtectedRoute><VideosPage /></ProtectedRoute>} />
        <Route path="/resumen-semanal" element={<ProtectedRoute><WeeklySummaryPage /></ProtectedRoute>} />
        <Route path="/pizarra" element={<ProtectedRoute><TacticalBoardPage /></ProtectedRoute>} />
        <Route path="/pizarras" element={<ProtectedRoute><TacticsLibraryPage /></ProtectedRoute>} />
        <Route path="/vestuario" element={<ProtectedRoute><VestuarioPage /></ProtectedRoute>} />
        <Route path="/nutricion" element={<ProtectedRoute><NutritionELearningPage /></ProtectedRoute>} />
        <Route path="/elearning" element={<ProtectedRoute><NutritionELearningPage /></ProtectedRoute>} />
        <Route path="/asistencia" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
        <Route path="/ajustes" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
        <Route path="/biblioteca" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
        <Route path="/coordinador" element={<ProtectedRoute><CoordinatorDashboard /></ProtectedRoute>} />
        <Route path="/enterprise/superadmin" element={<ProtectedRoute><EnterpriseSuperadminPage /></ProtectedRoute>} />
        <Route path="/enterprise/academy/:academyId" element={<ProtectedRoute><AcademyDetailPage /></ProtectedRoute>} />
        <Route path="/enterprise/formation" element={<ProtectedRoute><EnterpriseFormationPage /></ProtectedRoute>} />
        <Route path="/enterprise/notifications" element={<ProtectedRoute><EnterpriseNotificationsPage /></ProtectedRoute>} />
        <Route path="/enterprise/payments" element={<ProtectedRoute><EnterprisePaymentsPage /></ProtectedRoute>} />
        <Route path="/enterprise/metrics" element={<ProtectedRoute><EnterpriseMetricsPage /></ProtectedRoute>} />
        <Route path="/organizacion" element={<ProtectedRoute><OrganizationSettings /></ProtectedRoute>} />
        <Route path="/padres" element={<ProtectedRoute><ParentsPanelPage /></ProtectedRoute>} />

        {/* ── Portal de Padres ──────────────────────────────────────────── */}
        {/* Rutas públicas del portal */}
        <Route path="/parent/invite/:token" element={<ParentInvitePage />} />
        <Route path="/parent/acceso"        element={<ParentLoginPage />} />

        {/* Rutas protegidas del portal de padres */}
        <Route path="/parent/mensajes"      element={<ParentRoute><ParentHomePage /></ParentRoute>} />
        <Route path="/parent/mensajes/:id"  element={<ParentRoute><ParentMessageDetail /></ParentRoute>} />
        <Route path="/parent/partido"       element={<ParentRoute><ParentMatchCallPage /></ParentRoute>} />
        <Route path="/parent/partido/:id"   element={<ParentRoute><ParentMatchCallPage /></ParentRoute>} />
        <Route path="/parent/ajustes"       element={<ParentRoute><ParentSettingsPage /></ParentRoute>} />
        {/* Redirigir /parent → /parent/mensajes */}
        <Route path="/parent"               element={<Navigate to="/parent/mensajes" replace />} />
        {/* 404 del portal de padres */}
        <Route path="/parent/*"             element={<ParentRoute><ParentNotFoundPage /></ParentRoute>} />
        {/* ─────────────────────────────────────────────────────────────── */}

        {/* / is handled above */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

// ─── Guard del portal de padres ───────────────────────────────────────────────
const ParentRoute = ({ children }) => {
  const { isAuthenticated } = useParentAuth();
  if (!isAuthenticated) {
    return <Navigate to="/parent/acceso" replace />;
  }
  return <ErrorBoundary>{children}</ErrorBoundary>;
};

function App() {
  // Reaplica la decisión de cookies ya tomada (carga PostHog solo si se aceptó).
  // Si no hay decisión previa no se carga nada: lo pide CookieBanner.
  React.useEffect(() => { applyStoredConsent(); }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ParentAuthProvider>
            <TeamProvider>
              <EntitlementsProvider>
              <AppRoutes />
              </EntitlementsProvider>
              <Toaster
                position="top-right"
                toastOptions={{
                  style: {
                    background: '#18181b',
                    border: '1px solid #27272a',
                    color: '#fafafa',
                    fontSize: '14px',
                  }
                }}
              />
              {/* Va dentro del Router: usa <Link> hacia /cookies y /privacidad */}
              <CookieBanner />
              {/* Ofrece instalar la PWA. Se calla solo en nativo, ya instalada
                  o si el usuario lo pospuso. */}
              <InstallBanner />
            </TeamProvider>
          </ParentAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
